import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
  id: number;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface VisitorChatbotProps {
  designTokens: {
    bg: string;
    bg2: string;
    bg3: string;
    border: string;
    lime: string;
    white: string;
    gray1: string;
    gray2: string;
    gray3: string;
  };
}

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const VisitorChatbot: React.FC<VisitorChatbotProps> = ({ designTokens: C }) => {
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: 1, type: 'ai', content: "Hello! I'm the EIS AI Assistant. Ask me anything about our compliance platform \u2014 frameworks, features, how it works, or how we can help your organisation.", timestamp: new Date() }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatStatus, setChatStatus] = useState<'ai' | 'pending_human' | 'assigned'>('ai');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Stable session ID for this conversation
  const sessionIdRef = useRef(`visitor_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const lastMsgIdRef = useRef(0);

  const sendToBackend = async (userText: string, _currentMessages: Message[]): Promise<{
    message: string;
    status?: string;
    conversation_id?: string;
    suppress_display?: boolean;
  }> => {
    const response = await fetch('/api/ai/livechat/message', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      body: JSON.stringify({
        message: userText,
        session_id: sessionIdRef.current,
      })
    });
    return await response.json();
  };

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Poll for agent messages and status changes when we have a conversation
  useEffect(() => {
    if (!conversationId) return;

    const poll = async () => {
      try {
        const resp = await fetch(`/api/ai/livechat/poll/${conversationId}?after=${lastMsgIdRef.current}`);
        const data = await resp.json();

        // Process events (agent_connected, transfer, resolved)
        if (data.events?.length > 0) {
          for (const evt of data.events) {
            if (evt.event === 'agent_connected' || evt.event === 'new_message' || evt.event === 'transfer' || evt.event === 'conversation_resolved') {
              if (evt.data?.message || evt.data?.content) {
                setMessages(prev => [...prev, {
                  id: Date.now() + Math.random(),
                  type: 'ai' as const,
                  content: evt.data.message || evt.data.content,
                  timestamp: new Date()
                }]);
              }
            }
          }
        }

        // Process new agent/system messages
        if (data.messages?.length > 0) {
          for (const msg of data.messages) {
            if (msg.id > lastMsgIdRef.current) {
              lastMsgIdRef.current = msg.id;
              setMessages(prev => {
                // Deduplicate by content+time
                const exists = prev.some(m => m.content === msg.content && Math.abs(new Date(m.timestamp).getTime() - new Date(msg.created_at).getTime()) < 5000);
                if (exists) return prev;
                return [...prev, {
                  id: msg.id,
                  type: 'ai' as const,
                  content: msg.content,
                  timestamp: new Date(msg.created_at)
                }];
              });
            }
          }
        }

        // Update status
        if (data.status) setChatStatus(data.status);
      } catch {
        // Silent
      }
    };

    pollTimerRef.current = setInterval(poll, 3000);
    poll();

    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [conversationId]);

  // Tooltip visibility cycle - shows periodically to attract attention
  useEffect(() => {
    if (widgetOpen) {
      setShowTooltip(false);
      return;
    }

    const cycleTooltip = () => {
      // Show tooltip after delay
      const showTimer = setTimeout(() => {
        setShowTooltip(true);
        // Hide after showing for a while
        const hideTimer = setTimeout(() => {
          setShowTooltip(false);
          // Schedule next cycle
          tooltipTimerRef.current = setTimeout(cycleTooltip, 15000); // 15s before next cycle
        }, 4000); // Show for 4 seconds
        tooltipTimerRef.current = hideTimer;
      }, 5000); // Wait 5s before first show
      tooltipTimerRef.current = showTimer;
    };

    cycleTooltip();

    return () => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    };
  }, [widgetOpen]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    const userText = input.trim();
    const userMessage: Message = { id: Date.now(), type: 'user', content: userText, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const data = await sendToBackend(userText, []);

      // Track conversation ID for polling
      if (data.conversation_id && !conversationId) {
        setConversationId(data.conversation_id);
      }

      // Update status from server
      if (data.status) setChatStatus(data.status as any);

      // Show message unless suppressed
      if (!data.suppress_display && data.message) {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: data.message,
          timestamp: new Date()
        }]);
      }
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'ai', content: 'Sorry, I encountered a connection issue. Please try again or contact us at support@eis-tech.co.uk.', timestamp: new Date() }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickAction = async (action: string) => {
    if (isTyping) return;
    setInput(action);
    setTimeout(() => {
      setInput('');
      // Re-use handleSend logic by setting input and triggering
      const userMessage: Message = { id: Date.now(), type: 'user', content: action, timestamp: new Date() };
      setMessages(prev => [...prev, userMessage]);
      setIsTyping(true);
      sendToBackend(action, []).then(data => {
        if (data.conversation_id && !conversationId) setConversationId(data.conversation_id);
        if (data.status) setChatStatus(data.status as any);
        if (!data.suppress_display && data.message) {
          setMessages(prev => [...prev, { id: Date.now() + 1, type: 'ai', content: data.message, timestamp: new Date() }]);
        }
      }).catch(() => {
        setMessages(prev => [...prev, { id: Date.now() + 1, type: 'ai', content: 'Connection issue. Please try again.', timestamp: new Date() }]);
      }).finally(() => setIsTyping(false));
    }, 0);
  };

  return (
    <>
      <style>{`
        .hover-scroll {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
        }
        .hover-scroll:hover {
          scrollbar-color: ${C.gray3} transparent;
        }
        .hover-scroll::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .hover-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .hover-scroll::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
        }
        .hover-scroll:hover::-webkit-scrollbar-thumb {
          background: ${C.gray3};
        }
        .hover-scroll::-webkit-scrollbar-thumb:hover {
          background: ${C.gray2};
        }
        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.6);
            opacity: 0;
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        .ai-button-pulse::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background-color: ${C.lime};
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          z-index: -1;
        }
        .ai-button-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
        {/* Floating Action Button */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {/* Tooltip label */}
          <AnimatePresence>
            {!widgetOpen && showTooltip && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: 'absolute',
                  right: 70,
                  backgroundColor: C.bg,
                  color: C.white,
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  border: `1px solid ${C.border}`
                }}
              >
              Need help?
              <div style={{
                position: 'absolute',
                right: -6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderLeft: `6px solid ${C.border}`
              }}></div>
              </motion.div>
            )}
          </AnimatePresence>
          <motion.button
            onClick={() => setWidgetOpen(!widgetOpen)}
            className={!widgetOpen ? 'ai-button-pulse ai-button-float' : ''}
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: C.lime,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 32px ${C.lime}40`,
              zIndex: 1001,
              position: 'relative'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
          {widgetOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 12h8" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
              <path d="M9 9h6" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
              <path d="M9 15h6" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </motion.button>
        </div>

        {/* Chat Widget */}
        <AnimatePresence>
          {widgetOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                bottom: 80,
                right: 0,
                width: 380,
                height: 500,
                backgroundColor: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 16,
                boxShadow: `0 20px 60px rgba(0,0,0,0.3)`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* Header */}
              <div style={{
                padding: '16px 20px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  backgroundColor: C.lime,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <div style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>AI Assistant</div>
                  <div style={{ color: C.gray2, fontSize: 12 }}>EIS Tech Support</div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      backgroundColor: C.lime,
                      borderRadius: '50%',
                      animation: 'pulse 2s infinite'
                    }}></div>
                    <span style={{ color: C.gray2, fontSize: 12 }}>Online</span>
                  </div>
                  <a href="https://yiontech.co.uk" target="_blank" rel="noopener noreferrer" style={{ color: C.gray3, fontSize: 9, fontWeight: 500, textDecoration: 'none' }}>Powered by YionTech</a>
                </div>
              </div>

              {/* Messages */}
              <div className="hover-scroll" style={{
                flex: 1,
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                overflowY: 'auto'
              }}>
                {messages.map((message) => (
                  <div key={message.id} style={{
                    display: 'flex',
                    justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
                  }}>
                    <div style={{
                      maxWidth: '80%',
                      padding: '12px 16px',
                      borderRadius: message.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      backgroundColor: message.type === 'user' ? C.lime : C.bg2,
                      color: message.type === 'user' ? '#000' : C.white,
                      fontSize: 14,
                      lineHeight: 1.4
                    }}>
                      {message.content}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '16px 16px 16px 4px',
                      backgroundColor: C.bg2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <div style={{ color: C.gray2, fontSize: 14 }}>AI is typing</div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <div style={{ width: 4, height: 4, backgroundColor: C.lime, borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out' }}></div>
                        <div style={{ width: 4, height: 4, backgroundColor: C.lime, borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out 0.2s' }}></div>
                        <div style={{ width: 4, height: 4, backgroundColor: C.lime, borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out 0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Actions - Only show at start */}
              {messages.length <= 1 && (
                <div
                  className="hover-scroll"
                  style={{
                    padding: '12px 20px',
                    borderTop: `1px solid ${C.border}`,
                    display: 'flex',
                    gap: 8,
                    overflowX: 'auto'
                  }}
                >
                  {[
                    'What standards do you support?',
                    'How does the platform work?',
                    'Book a demo',
                    'Speak to someone'
                  ].map((action) => (
                    <button
                      key={action}
                      onClick={() => handleQuickAction(action)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: C.bg2,
                        border: `1px solid ${C.border}`,
                        borderRadius: 20,
                        color: C.gray1,
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = C.bg3;
                        e.currentTarget.style.borderColor = C.lime;
                        e.currentTarget.style.color = C.white;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = C.bg2;
                        e.currentTarget.style.borderColor = C.border;
                        e.currentTarget.style.color = C.gray1;
                      }}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div style={{
                padding: '16px 20px',
                borderTop: `1px solid ${C.border}`,
                display: 'flex',
                gap: 8
              }}>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Ask me anything about EIS Tech..."
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    border: `1px solid ${C.border}`,
                    borderRadius: 24,
                    backgroundColor: C.bg2,
                    color: C.white,
                    fontSize: 14,
                    outline: 'none'
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: input.trim() && !isTyping ? C.lime : C.bg2,
                    border: 'none',
                    cursor: input.trim() && !isTyping ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke={input.trim() && !isTyping ? '#000' : C.gray3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              {/* AI disclaimer */}
              <div style={{ padding: '6px 20px 10px', borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: 10, color: C.gray3, textAlign: 'center', margin: 0, lineHeight: 1.4 }}>
                  Responses may be generated by AI and may be reviewed for quality and support purposes.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <style>{`
        @keyframes bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
      `}</style>
    </>
  );
};

export default VisitorChatbot;
